<script setup lang="ts">
import { computed } from "vue";
import { vElementVisibility } from "@vueuse/components";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VDivider from "@magnit/core/src/components/VDivider/VDivider.vue";
import {
  type IPromoProduct,
  IPromoProductProgressiveDiscountType,
  type IPromoProductProgressiveDiscountValue,
} from "@magnit/unit-catalog/src/unit-catalog.types";
import { formatPrice } from "@magnit/unit-catalog/src/utils/converter";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import { collectCardDetailProgressiveDiscountPayload } from "../utils/analytics";
import type { ICatalogBFFDetailedGood } from "../types/goods";
import type { AnalyticsEventArguments } from "../types/analytics";

const props = defineProps<{
  product: ICatalogBFFDetailedGood | IPromoProduct;
  type?: IPromoProductProgressiveDiscountType;
  variants?: IPromoProductProgressiveDiscountValue[];
  categoryName: string;
  noCaption?: boolean;
}>();

const modelValue = defineModel<IPromoProductProgressiveDiscountValue | null>();

const emit = defineEmits<{
  analytics: AnalyticsEventArguments;
}>();

const showProgressive = computed(
  () => props.type && props.variants && props.variants.length && modelValue,
);

const getVariantString = (value: number) => {
  switch (props.type) {
    case IPromoProductProgressiveDiscountType.PRICE:
      return `${formatPrice(value / 100)}/шт`;
    case IPromoProductProgressiveDiscountType.CASHBACK:
      return `+${value}% бонусов`;
    case IPromoProductProgressiveDiscountType.BONUSES:
      return `+${value} бонусами`;
    case IPromoProductProgressiveDiscountType.MAGNETS:
      return `+${value} магнитиками`;
    default:
      return "";
  }
};

const onClick = (variant: IPromoProductProgressiveDiscountValue) => {
  modelValue.value = variant;
  if (!props.product) return;
  emit(
    "analytics",
    "CardDetail:ProgressiveDiscount:Click",
    collectCardDetailProgressiveDiscountPayload(
      props.product,
      variant,
      props.type,
      props.categoryName,
    ),
  );
};

const onVisibility = (
  visible: boolean,
  variant: IPromoProductProgressiveDiscountValue,
) => {
  if (!visible || !props.product) return;
  emit(
    "analytics",
    "CardDetail:ProgressiveDiscount:View",
    collectCardDetailProgressiveDiscountPayload(
      props.product,
      variant,
      props.type,
      props.categoryName,
    ),
  );
};

const isVariantActive = (variant: IPromoProductProgressiveDiscountValue) =>
  variant.count === modelValue.value?.count;

const getTextColor = (variant: IPromoProductProgressiveDiscountValue) =>
  isVariantActive(variant) ? "invert-primary" : "primary";
</script>

<template>
  <div v-if="showProgressive" class="progressive-discount">
    <VHidden
      class="progressive-discount__divider"
      :hide="{ xs: false, l: true }"
    >
      <VDivider :thickness="4" />
    </VHidden>
    <VText v-if="!props.noCaption" font="caption-large-accent" color="secondary"
      >Количество</VText
    >
    <div class="progressive-discount__variants">
      <div
        v-for="variant in variants"
        :key="variant.count"
        v-element-visibility="
          (visible: boolean) =>
            onVisibility(
              visible,
              variant as IPromoProductProgressiveDiscountValue,
            )
        "
        class="progressive-discount_variant"
        :class="{
          'progressive-discount_variant--active': isVariantActive(variant),
        }"
        @click="onClick(variant)"
      >
        <VText font="body-small-accent" :color="getTextColor(variant)">
          От {{ variant.count }} шт
        </VText>
        <VText
          v-if="type === IPromoProductProgressiveDiscountType.PERCENT"
          font="caption-large-regular-low"
          :color="getTextColor(variant)"
        >
          {{ variant.label }}
        </VText>
        <VText
          v-else
          font="caption-large-regular-low"
          :color="getTextColor(variant)"
        >
          <span v-html="getVariantString(variant.value)" />
        </VText>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.progressive-discount {
  margin-bottom: var(--pl-unit-x4);

  @media (--pl-viewport-from-l) {
    margin-bottom: 0;
  }

  &__title {
    margin-bottom: var(--pl-unit-x2);
  }

  &__variants {
    margin: 0 calc(-1 * var(--pl-wrapper-indent));
    padding: 0 var(--pl-wrapper-indent);
    display: flex;
    gap: var(--pl-unit-x2);
    padding-top: var(--pl-unit-x2);
    padding-bottom: var(--pl-unit-x4);
    margin-bottom: calc(-1 * var(--pl-unit-x4));
    overflow: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @media (--pl-viewport-from-ml) {
      flex-wrap: wrap;
    }
  }

  &_variant {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    padding: var(--pl-unit-x2) var(--pl-unit-x3);
    border-radius: var(--pl-field-radius-m);
    background: var(--pl-surface-primary-default);
    transition: background 0.3s;
    cursor: pointer;
    box-shadow: var(--pl-shadow-ml);

    &--active {
      background: var(--pl-chips-background-selected-default);
      color: var(--pl-text-invert-primary);
    }
  }

  &__divider {
    margin: var(--pl-unit-x4) calc(-1 * var(--pl-wrapper-indent))
      var(--pl-unit-x4) calc(-1 * var(--pl-wrapper-indent));
  }
}
</style>
